import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { PageTitle } from "@/components/PageTitle";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { User, FileText, Activity, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

interface SearchResult {
  id: string;
  type: "patient" | "document" | "record";
  title: string;
  subtitle?: string;
  description?: string;
  url: string;
  patientName?: string;
  date?: string;
}

export default function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("q") || "";
  const [searchQuery, setSearchQuery] = useState(query);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query.length >= 2) {
      performSearch(query);
    }
  }, [query]);

  const performSearch = async (searchQuery: string) => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const searchTerm = `%${searchQuery.toLowerCase()}%`;
      const searchResults: SearchResult[] = [];

      // Search patients
      const { data: patients } = await supabase
        .from("patients_onboarding")
        .select("id, full_name, cpf, email")
        .eq("owner_id", user.id)
        .or(`full_name.ilike.${searchTerm},cpf.ilike.${searchTerm}`)
        .limit(20);

      if (patients) {
        patients.forEach(p => {
          searchResults.push({
            id: p.id,
            type: "patient",
            title: p.full_name,
            subtitle: p.cpf || p.email || "",
            url: `/app/pacientes/${p.id}`
          });
        });
      }

      // Search documents
      const { data: documents } = await supabase
        .from("documents_onboarding")
        .select(`
          id, 
          file_name, 
          created_at,
          ai_summary,
          patient_id,
          patients_onboarding!inner(full_name)
        `)
        .eq("owner_id", user.id)
        .or(`file_name.ilike.${searchTerm},ai_summary.ilike.${searchTerm}`)
        .limit(20);

      if (documents) {
        documents.forEach(d => {
          searchResults.push({
            id: d.id,
            type: "document",
            title: d.file_name,
            subtitle: d.ai_summary?.substring(0, 100) || "",
            patientName: d.patients_onboarding?.full_name,
            date: new Date(d.created_at).toLocaleDateString('pt-BR'),
            url: `/app/documentos/${d.id}`
          });
        });
      }

      // ✅ REMOVIDO: patient_health_records não é mais usado
      // Código removido - não buscamos mais registros em patient_health_records

      setResults(searchResults);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim().length >= 2) {
      setSearchParams({ q: searchQuery });
    }
  };

  const getIcon = (type: SearchResult["type"]) => {
    switch(type) {
      case "patient": return <User className="h-5 w-5" />;
      case "document": return <FileText className="h-5 w-5" />;
      case "record": return <Activity className="h-5 w-5" />;
    }
  };

  const getCategoryLabel = (type: SearchResult["type"]) => {
    switch(type) {
      case "patient": return "Paciente";
      case "document": return "Documento";
      case "record": return "Registro de Saúde";
    }
  };

  const groupedResults = {
    patient: results.filter(r => r.type === "patient"),
    document: results.filter(r => r.type === "document"),
    record: results.filter(r => r.type === "record")
  };

  const totalResults = results.length;

  return (
    <div className="space-y-6">
      <div>
        <PageTitle>Pesquisa</PageTitle>
        <p className="text-muted-foreground mt-2">
          {query ? `Resultados para "${query}"` : "Digite para buscar"}
        </p>
      </div>

      <form onSubmit={handleSearch}>
        <div className="relative max-w-2xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            placeholder="Buscar pacientes, documentos, registros..."
            className="pl-10 pr-4 h-12 text-base"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
        </div>
      </form>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : totalResults === 0 && query.length >= 2 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Search className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhum resultado encontrado</h3>
            <p className="text-muted-foreground">
              Tente buscar com outros termos ou verifique a ortografia
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {groupedResults.patient.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Pacientes ({groupedResults.patient.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {groupedResults.patient.map((result) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => navigate(result.url)}
                    className="w-full flex items-center gap-4 p-4 rounded-lg hover:bg-muted transition-colors text-left border"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                      {getIcon(result.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{result.title}</div>
                      {result.subtitle && (
                        <div className="text-sm text-muted-foreground truncate">
                          {result.subtitle}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          {groupedResults.document.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Documentos ({groupedResults.document.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {groupedResults.document.map((result) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => navigate(result.url)}
                    className="w-full flex items-center gap-4 p-4 rounded-lg hover:bg-muted transition-colors text-left border"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-600">
                      {getIcon(result.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{result.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {result.patientName && `Paciente: ${result.patientName}`}
                        {result.date && ` • ${result.date}`}
                      </div>
                      {result.subtitle && (
                        <div className="text-xs text-muted-foreground truncate mt-1">
                          {result.subtitle}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          {groupedResults.record.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Registros de Saúde ({groupedResults.record.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {groupedResults.record.map((result) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => navigate(result.url)}
                    className="w-full flex items-center gap-4 p-4 rounded-lg hover:bg-muted transition-colors text-left border"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center text-green-600">
                      {getIcon(result.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{result.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {result.patientName && `Paciente: ${result.patientName}`}
                        {result.date && ` • ${result.date}`}
                        {result.subtitle && ` • ${result.subtitle}`}
                      </div>
                      {result.description && (
                        <div className="text-xs text-muted-foreground truncate mt-1">
                          {result.description}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
